import {
  ensureFoundationAllInSelections,
  isFoundationAllSelection,
} from "./foundationScope";
import {
  findTemplateForServiceCategory,
  normalizeQuestionnaireTemplateLabel,
} from "./templateToServiceCategory";
import { getVendorListedServiceCategories } from "./vendorProfileCapabilities";

/** Map questionnaire API rows to VQQ template dropdown options. */
export function buildVqqTemplateOptions(questionnaires = []) {
  return (questionnaires || [])
    .filter((item) => item?.name !== "Core RFI")
    .map((item) => ({
      value: item?.hash_id,
      label: normalizeQuestionnaireTemplateLabel(item?.name),
      global: item?.global,
      owner_id: item?.id,
      hash_id: item?.hash_id,
      template_id: item?.template_id ?? item?.hc_template_id ?? null,
    }))
    .filter((item) => item.value && item.label);
}

export function normalizeTemplateSearchText(text) {
  return String(text ?? "")
    .trim()
    .replace(/^Diligent\s+/i, "")
    .toLowerCase();
}

function templateOptionMatchesId(option, templateId) {
  const id = String(templateId ?? "").trim();
  if (!id || !option) return false;
  return (
    String(option.value ?? "") === id ||
    String(option.owner_id ?? "") === id ||
    String(option.hash_id ?? "") === id ||
    String(option.template_id ?? "") === id
  );
}

function questionnaireMatchesId(questionnaire, templateId) {
  const id = String(templateId ?? "").trim();
  if (!id || !questionnaire) return false;
  return (
    String(questionnaire.hash_id ?? "") === id ||
    String(questionnaire.id ?? "") === id ||
    String(questionnaire.template_id ?? "") === id ||
    String(questionnaire.hc_template_id ?? "") === id
  );
}

/** Resolve a VQQ template option from URL params once the template catalog is loaded. */
export function findVqqTemplateFromUrlParams(
  { templateId, templateName },
  options = [],
  questionnaires = [],
) {
  if (!options.length) return null;

  const tid = String(templateId ?? "").trim();
  if (tid) {
    const direct = options.find((option) => templateOptionMatchesId(option, tid));
    if (direct) return direct;

    const raw = (questionnaires || []).find((row) => questionnaireMatchesId(row, tid));
    if (raw?.hash_id) {
      return options.find((option) => option.value === raw.hash_id) || null;
    }
  }

  const name = String(templateName ?? "").trim();
  if (!name) return null;

  const normalizedName = normalizeTemplateSearchText(name);
  return (
    options.find((option) => {
      const label = String(option.label ?? "").trim();
      return (
        label === name ||
        normalizeTemplateSearchText(label) === normalizedName ||
        label.toLowerCase() === name.toLowerCase()
      );
    }) || null
  );
}

/** Map vendor profile capabilities to VQQ template options (1 template per category). */
export function resolveSuggestedVqqTemplates(vendorEntries = [], templateOptions = []) {
  const templates = [];
  const seen = new Set();

  vendorEntries.forEach(({ name, capability }) => {
    const template = findTemplateForServiceCategory(name, templateOptions, capability);
    if (!template?.value || seen.has(template.value)) return;
    seen.add(template.value);
    templates.push({
      value: template.value,
      label: template.label,
      global: template.global,
      owner_id: template.owner_id,
    });
  });

  return templates;
}

/** Map vendor profile service category names to VQA dropdown entries. */
export function resolveSuggestedVqaCategories(vendorCategoryNames = [], v2Options = []) {
  const validNames = new Set(
    (v2Options || []).filter((option) => !option.isGroupHeader).map((option) => option.value),
  );
  const nameToId = {};
  (v2Options || []).forEach((option) => {
    if (!option.isGroupHeader && option.rfiId) {
      nameToId[option.value] = option.rfiId;
    }
  });

  return (vendorCategoryNames || [])
    .filter((name) => validNames.has(name) && nameToId[name])
    .map((name) => ({ value: nameToId[name], label: name }));
}

export function resolveSuggestedVqqTemplatesFromProvider(provider, templateOptions = []) {
  const vendorEntries = getVendorListedServiceCategories(provider);
  return resolveSuggestedVqqTemplates(vendorEntries, templateOptions);
}

/** Template option values that should show the Suggested chip (1:1 with listed services). */
export function getSuggestedVqqTemplateValueSet(provider, templateOptions = []) {
  return new Set(
    resolveSuggestedVqqTemplatesFromProvider(provider, templateOptions).map(
      (template) => template.value,
    ),
  );
}

/**
 * VQA option identities that should show the Suggested chip.
 * Matches by rfi_id and by category name (option.value).
 */
export function getSuggestedVqaIdentitySet(vendorCategoryNames = [], v2Options = []) {
  const suggested = resolveSuggestedVqaCategories(vendorCategoryNames, v2Options);
  const identities = new Set();
  suggested.forEach((entry) => {
    if (entry?.value != null && entry.value !== "") identities.add(entry.value);
    if (entry?.label) identities.add(entry.label);
  });
  return identities;
}

export function hasMeaningfulServiceSelection(selectedServices = [], mode = "vqq") {
  if (!selectedServices?.length) return false;
  if (mode === "vqa") {
    return selectedServices.some((item) => !isFoundationAllSelection(item));
  }
  return true;
}

function mergeUniqueServiceSelections(existing = [], additions = []) {
  const seen = new Set((existing || []).map((item) => item.value));
  const merged = [...(existing || [])];
  (additions || []).forEach((item) => {
    if (!item?.value || seen.has(item.value)) return;
    seen.add(item.value);
    merged.push(item);
  });
  return merged;
}

/**
 * Apply URL template preselection from document library navigation once the catalog loads.
 * Does not auto-select vendor profile services — those remain manual suggestions in the UI.
 */
export function createRequestFormServicePreselection({
  mode,
  provider,
  providerId,
  urlTemplateId,
  urlTemplateName,
  questionnairesLoading,
  questionnaires = [],
  templateOptions,
  v2Options,
  rfisLoading,
  isLoadingCapabilities,
  resolvedProvider,
  vendorCategoryNames,
  selectedServices,
  setServiceCategories,
  userEditedRef,
  lastAppliedProviderRef,
  urlTemplateAppliedRef,
}) {
  const normalizedProviderId =
    providerId != null && providerId !== "" ? String(providerId) : null;

  let providerJustChanged = false;
  if (normalizedProviderId !== lastAppliedProviderRef.current) {
    providerJustChanged = lastAppliedProviderRef.current != null;
    const hadPreviousProvider = lastAppliedProviderRef.current != null;
    lastAppliedProviderRef.current = normalizedProviderId;
    userEditedRef.current = false;
    if (hadPreviousProvider) {
      setServiceCategories([]);
      // Keep URL template from re-applying after the user picks a different vendor.
      urlTemplateAppliedRef.current = true;
    }
  }

  if (!normalizedProviderId || !provider || userEditedRef.current) {
    return { applied: false };
  }

  if (isLoadingCapabilities) {
    return { applied: false, waitingFor: "capabilities" };
  }

  if (
    mode === "vqq" &&
    (urlTemplateId || urlTemplateName) &&
    !providerJustChanged
  ) {
    if (questionnairesLoading || !templateOptions?.length) {
      return { applied: false, waitingFor: "questionnaires" };
    }
    if (!urlTemplateAppliedRef.current) {
      const match = findVqqTemplateFromUrlParams(
        { templateId: urlTemplateId, templateName: urlTemplateName },
        templateOptions,
        questionnaires,
      );
      if (match) {
        setServiceCategories([match]);
        urlTemplateAppliedRef.current = true;
        return { applied: true, source: "url-template" };
      }
    }
    if (
      urlTemplateAppliedRef.current ||
      hasMeaningfulServiceSelection(selectedServices, mode)
    ) {
      return { applied: false };
    }
  }

  return { applied: false };
}

export function mergeVendorSuggestedServices({
  mode,
  selectedServices,
  suggestedServices,
}) {
  if (!suggestedServices?.length) return selectedServices || [];
  if (mode === "vqa") {
    return ensureFoundationAllInSelections(
      mergeUniqueServiceSelections(selectedServices, suggestedServices),
    );
  }
  return mergeUniqueServiceSelections(selectedServices, suggestedServices);
}
